import React, { useState, useEffect, useCallback } from "react";
import { X, RefreshCw, Plus, Smartphone, ChefHat, Copy, Check, Link } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import api from "../utils/api";
import { toast } from "react-toastify";
import { formatIndianCurrency } from "../utils/Const";

const PLATFORM_COLORS = {
  SWIGGY:  { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-500" },
  ZOMATO:  { bg: "bg-red-50",    border: "border-red-200",    text: "text-red-700",    badge: "bg-red-500" },
  MANUAL:  { bg: "bg-slate-50",  border: "border-slate-200",  text: "text-slate-700",  badge: "bg-slate-500" },
};

const STATUS_LABEL = { ORDER_PLACED: "New", PREPARING: "Preparing", READY: "Ready", SERVED: "Served" };

const BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title="Copy URL"
      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors"
    >
      {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
    </button>
  );
}

export default function OnlineOrdersPanel({ onClose, onSelectOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [form, setForm] = useState({ platform: "MANUAL", customerName: "", customerMobile: "", customerAddress: "", notes: "", items: [{ name: "", qty: 1, price: "" }] });
  const [submitting, setSubmitting] = useState(false);

  const restaurantId = (() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const decoded = jwtDecode(token);
      return decoded?.user?.id || null;
    } catch { return null; }
  })();

  // The key authenticates the aggregator; without it the server rejects the webhook
  const [webhookKey, setWebhookKey] = useState("");
  const keyParam = webhookKey ? `?key=${webhookKey}` : "";
  const swiggyUrl  = restaurantId ? `${BASE_URL}/webhooks/swiggy/${restaurantId}${keyParam}`  : null;
  const zomatoUrl  = restaurantId ? `${BASE_URL}/webhooks/zomato/${restaurantId}${keyParam}`  : null;

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get("/online-orders");
      setOrders(res.data.data || []);
    } catch {
      toast.error("Failed to load online orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    if (restaurantId) {
      api.get(`/user/${restaurantId}`).then(res => {
        setWebhookEnabled(Boolean(res.data?.webhookSync?.enabled));
        setWebhookKey(res.data?.webhookSync?.secret || "");
      }).catch(() => {});
    }
  }, [fetchOrders, restaurantId]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { name: "", qty: 1, price: "" }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setForm(f => ({ ...f, items: f.items.map((item, idx) => idx === i ? { ...item, [field]: val } : item) }));

  const handleSubmit = async () => {
    const validItems = form.items.filter(i => i.name.trim() && i.qty > 0 && parseFloat(i.price) > 0);
    if (validItems.length === 0) { toast.error("Add at least one item"); return; }
    setSubmitting(true);
    try {
      await api.post("/online-orders", {
        ...form,
        items: validItems.map(i => ({ name: i.name, qty: Number(i.qty), price: parseFloat(i.price) })),
      });
      toast.success("Online order created");
      setShowNew(false);
      setForm({ platform: "MANUAL", customerName: "", customerMobile: "", customerAddress: "", notes: "", items: [{ name: "", qty: 1, price: "" }] });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
              <Smartphone size={15} className="text-orange-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Online Orders</h2>
              <p className="text-[10px] text-slate-400">Swiggy · Zomato · Manual entry</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchOrders} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <RefreshCw size={13} />
            </button>
            <button
              onClick={() => setShowNew(v => !v)}
              className="flex items-center gap-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-xl transition-colors"
            >
              <Plus size={12} /> New Order
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* New Order Form */}
        {showNew && (
          <div className="border-b border-slate-100 px-5 py-4 space-y-3 bg-slate-50/50">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Enter order details</p>

            {/* Platform */}
            <div className="flex gap-2">
              {["MANUAL", "SWIGGY", "ZOMATO"].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, platform: p }))}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-xl border transition-colors ${form.platform === p ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                >
                  {p === "MANUAL" ? "Manual" : p.charAt(0) + p.slice(1).toLowerCase()}
                </button>
              ))}
            </div>

            {/* Customer */}
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Customer name" value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
              <input placeholder="Mobile" value={form.customerMobile} onChange={e => setForm(f => ({ ...f, customerMobile: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
            </div>
            <input placeholder="Delivery address" value={form.customerAddress} onChange={e => setForm(f => ({ ...f, customerAddress: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />

            {/* Items */}
            <div className="space-y-1.5">
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <input placeholder="Item name" value={item.name} onChange={e => updateItem(i, "name", e.target.value)}
                    className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
                  <input type="number" min="1" value={item.qty} onChange={e => updateItem(i, "qty", e.target.value)}
                    className="w-12 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-teal-500" />
                  <input type="number" min="0" placeholder="₹" value={item.price} onChange={e => updateItem(i, "price", e.target.value)}
                    className="w-20 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-teal-500" />
                  {form.items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="w-6 h-6 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 hover:bg-rose-200 shrink-0">
                      <X size={10} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addItem} className="text-xs font-bold text-teal-600 hover:text-teal-700">+ Add item</button>
            </div>

            <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting}
                className="px-4 py-1.5 text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-40 transition-colors">
                {submitting ? "Creating..." : "Create Order"}
              </button>
            </div>
          </div>
        )}

        {/* Orders List */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-10">
              <ChefHat size={32} className="mx-auto text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No pending online orders</p>
            </div>
          ) : (
            orders.map(order => {
              const plat = order.platform || "MANUAL";
              const colors = PLATFORM_COLORS[plat] || PLATFORM_COLORS.MANUAL;
              const itemCount = order.items?.length || 0;
              const total = order.totalAmount || order.subtotal || 0;
              return (
                <div key={order._id} className={`rounded-xl border p-3 ${colors.bg} ${colors.border}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-black text-white px-1.5 py-0.5 rounded-md ${colors.badge}`}>{plat}</span>
                      <span className="text-xs font-bold text-slate-700">#{order.orderNumber}</span>
                      {order.externalOrderId && (
                        <span className="text-[10px] text-slate-500">({order.externalOrderId})</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-white ${colors.text}`}>
                      {STATUS_LABEL[order.status] || order.status}
                    </span>
                  </div>
                  {order.customer?.name && (
                    <p className="text-xs text-slate-600 mt-1">{order.customer.name}{order.customer.mobile ? ` · ${order.customer.mobile}` : ""}</p>
                  )}
                  {order.customer?.address && (
                    <p className="text-[10px] text-slate-400 mt-0.5 truncate">{order.customer.address}</p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] text-slate-500">{itemCount} item{itemCount !== 1 ? "s" : ""} · ₹{formatIndianCurrency(total)}</p>
                    <button
                      onClick={() => onSelectOrder && onSelectOrder(order)}
                      className={`text-xs font-bold px-3 py-1 rounded-lg border ${colors.border} ${colors.text} bg-white hover:opacity-80 transition-opacity`}
                    >
                      Open in POS
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Webhook Setup Section — only shown when superadmin has enabled webhook sync */}
        {webhookEnabled && showWebhook && (
          <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/60 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Webhook setup</p>
              <button onClick={() => setShowWebhook(false)} className="text-[10px] text-slate-400 hover:text-slate-600">Hide</button>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Register these URLs in your Swiggy/Zomato partner portal under <strong>Webhook / Notify URL</strong>. Orders will appear here automatically.
            </p>

            {[
              { label: "Swiggy", url: swiggyUrl,  color: "text-orange-600" },
              { label: "Zomato", url: zomatoUrl,  color: "text-red-600"    },
            ].map(({ label, url, color }) => (
              <div key={label} className="space-y-1">
                <p className={`text-[10px] font-bold ${color}`}>{label}</p>
                {url ? (
                  <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                    <code className="flex-1 text-[10px] font-mono text-slate-700 break-all">{url}</code>
                    <CopyButton text={url} />
                  </div>
                ) : (
                  <p className="text-[10px] text-slate-400 italic">Login required to generate URL</p>
                )}
              </div>
            ))}

            <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <p className="text-[10px] text-amber-700 font-semibold">
                Your restaurant ID: <code className="font-mono font-bold">{restaurantId || "not found"}</code>
              </p>
              <p className="text-[10px] text-amber-600 mt-0.5">Share this with Swiggy/Zomato partner support if they need it.</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          {webhookEnabled ? (
            <button
              onClick={() => setShowWebhook(v => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
            >
              <Link size={12} />
              {showWebhook ? "Hide webhook URLs" : "Webhook setup (Swiggy / Zomato)"}
            </button>
          ) : (
            <span className="text-[10px] text-slate-400">Webhook sync disabled</span>
          )}
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
